import Attachment from "./Attachment";
import classNames from "@/utils/classNames";
import type { ReactNode } from "react";
import { PiWarningCircle } from "react-icons/pi";
import { ChatServerResponseContextFile } from "@/custolink-views/asset_frontend/asset_view/asset_components/ai_support/components/ChatView";
import "@/custolink-views/asset_frontend/redesigns/redesigns.css";

export type MessageProps = {
  id: string;
  sender: {
    id: string;
    name: string;
    avatarImageUrl?: string;
  };
  content?: string | ReactNode;
  contextFiles: Array<ChatServerResponseContextFile>;
  timestamp?: Date | number;
  type: "regular" | "reply" | "deleted" | "divider";
  attachments?: Array<{
    type: "image" | "video" | "audio" | "misc";
    source: File;
    mediaUrl: string;
  }>;
  isMyMessage?: boolean;
  bubbleClass?: string;
  error?: boolean;
  streaming?: boolean;
  customRenderer?: () => string | ReactNode;
  customAction?: () => string | ReactNode;
};

/**
 * One turn in the AI chat. A user turn is a compact accent-filled bubble,
 * right aligned, no avatar. An assistant turn carries no bubble at all - it
 * reads as a document column under a small accent-marked label ("AI
 * assistant"/the sender name, same voice as the rest of the AI support box),
 * with compact markdown typography (`.rda-chat-md`, see redesigns.css)
 * instead of Tailwind's oversized `prose` defaults.
 *
 * The copy action sits quietly under a successful answer and only firms up
 * on hover/focus (always visible on touch, where there is no hover to wait
 * for - see the `[@media(hover:none)]` escape below). A failed answer gets
 * the design's error tone and its own always-visible retry, supplied by
 * ChatView through the same `customAction` slot.
 */
const Message = (props: MessageProps) => {
  const {
    attachments,
    content,
    isMyMessage,
    type,
    customRenderer,
    customAction,
    bubbleClass,
    error,
    streaming,
    sender,
  } = props;

  if (type === "divider") {
    return null;
  }

  const actionNode = customAction ? customAction() : null;

  if (isMyMessage) {
    return (
      <div className="flex justify-end">
        <div
          className={classNames(
            "max-w-[85%] rounded-2xl bg-[var(--rda-accent)] px-4 py-2.5 text-[15px] leading-[1.45] text-[var(--rda-on-accent)]",
            bubbleClass,
          )}
        >
          {attachments && attachments.length > 0 && (
            <Attachment attachments={attachments} />
          )}
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="group flex w-full flex-col gap-1.5">
      <div className="flex items-center gap-1.5 text-xs font-medium text-[var(--rda-muted)]">
        <span
          aria-hidden
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--rda-accent)]"
        />
        <span>{sender.name}</span>
      </div>

      {error ? (
        <div
          className={classNames(
            "flex flex-col gap-2 rounded-xl bg-[#FCE8E6] px-3.5 py-3 text-sm text-[#7A1F17]",
            bubbleClass,
          )}
        >
          <div className="flex items-start gap-2">
            <PiWarningCircle aria-hidden className="mt-0.5 shrink-0 text-base" />
            <div>{customRenderer ? customRenderer() : content}</div>
          </div>
          {actionNode}
        </div>
      ) : (
        <>
          <div className={classNames("rda-chat-md", bubbleClass)}>
            {customRenderer ? customRenderer() : content}
            {streaming && <span aria-hidden className="rda-caret" />}
          </div>
          {actionNode && (
            <div
              className={classNames(
                "opacity-0 transition-opacity duration-150",
                "group-hover:opacity-100 group-focus-within:opacity-100",
                "[@media(hover:none)]:opacity-100",
              )}
            >
              {actionNode}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Message;
